const { parseEncodedObject } = window.ssUtils;
import { BaseCard } from './ss-BaseCard.v2.js';

// YouTube IFrame API readiness helper
let ytReadyResolve;
let ytReadyPromise = new Promise((resolve) => {
  ytReadyResolve = resolve;
});

// If API is already present, resolve immediately
if (window.YT && typeof window.YT.Player === 'function') {
  ytReadyResolve();
}

// Hook global callback used by the IFrame API
window.onYouTubeIframeAPIReady = function onYouTubeIframeAPIReady() {
  ytReadyResolve();
};

function ensureYouTubeApiScriptPresent() {
  if (!document.getElementById('yt-iframe-api')) {
    const tag = document.createElement('script');
    tag.id = 'yt-iframe-api';
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
  }
}

function ensurePlayerContainerExists() {
  const modal = document.getElementById('product-video-modal');
  if (!modal) return null;
  const content = modal.querySelector('.product-video-modal-content');
  if (!content) return null;
  let container = content.querySelector('#product-video-player');
  if (!container) {
    container = document.createElement('div');
    container.id = 'product-video-player';
    content.prepend(container);
  }
  return container;
}

async function createOrLoadYouTubePlayer(videoId, { mute } = { mute: true }) {
  ensureYouTubeApiScriptPresent();
  await ytReadyPromise;
  ensurePlayerContainerExists();

  // Always create a fresh instance for reliability
  try { window._productVideoPlayer?.stopVideo?.(); } catch (_) {}
  try { window._productVideoPlayer?.destroy?.(); } catch (_) {}
  window._productVideoPlayer = null;

  return new Promise((resolve) => {
    window._productVideoPlayer = new window.YT.Player('product-video-player', {
      videoId,
      width: '100%',
      height: '100%',
      playerVars: {
        autoplay: 1,
        controls: 0,
        rel: 0,
        modestbranding: 1,
        playsinline: 1,
        loop: 0,
        enablejsapi: 1,
      },
      events: {
        onReady: (event) => {
          if (mute) event.target.mute(); else event.target.unMute();
          event.target.playVideo();
          resolve(window._productVideoPlayer);
        },
        onStateChange: (event) => {
          // Manually loop a single video
          if (event.data === window.YT?.PlayerState?.ENDED) {
            try {
              event.target.seekTo(0, true);
              event.target.playVideo();
            } catch (_) {}
          }
        },
      },
    });
  });
}

function destroyYouTubePlayer() {
  try {
    if (window._productVideoPlayer) {
      window._productVideoPlayer.stopVideo?.();
      window._productVideoPlayer.destroy?.();
    }
  } catch (_) {}
  window._productVideoPlayer = null;
  // Recreate container to fully reset iframe
  const oldEl = document.getElementById('product-video-player');
  if (oldEl && oldEl.parentElement) {
    const replacement = document.createElement('div');
    replacement.id = 'product-video-player';
    oldEl.parentElement.replaceChild(replacement, oldEl);
  }
}

class ProductCards extends BaseCard {
  constructor(containerId) {
    super(containerId);
    // Bind handlers so we can reliably add/remove listeners
    this.handleAddToCartOpen = this.handleAddToCartOpen?.bind(this) || this.handleAddToCartOpen;
    this.handleAddToCartSubmit = this.handleAddToCartSubmit?.bind(this) || this.handleAddToCartSubmit;
  }

  noResults() {
    this.container.innerHTML = '<p>No products match your search...</p>';
  }

  renderSkeletons(count) {
    for (let i = 0; i < count; i++) {
      const skeleton = document.createElement('li');
      skeleton.classList.add('js__grid-item', 'product-card', 'skeleton');
      skeleton.innerHTML = `
        <div class="product-card-image">
          <div class="product-card-image-skeleton"></div>
        </div>
        <div class="product-card-content">
          <div class="product-card-title-skeleton"></div>
          <div class="product-card-price-skeleton"></div>
        </div>
      `;
      this.container.appendChild(skeleton);
    }
  }

  setupVideoAddToCartPDP(productMetaObj) {
    const { variants, price, featured_image, title } = productMetaObj;
    const processedVariants = this.processVariants(parseEncodedObject(variants));

    const [mainProductName, subProductName] = String(title || '').split(' - ');
    const productNameSection = document.querySelector('.product-video-add-to-cart-section .product-name');

    if (productNameSection) {
      // Convert price from cents to dollars and format to 2 decimal places
      const formattedPrice = (Number(price) / 100).toFixed(2);
      productNameSection.innerHTML = `
        <h3>${mainProductName || ''}</h3>
        ${subProductName ? `<p class="sub-name">${subProductName}</p>` : ''}
        <p class="product-price mt-0">$${formattedPrice} USD</p>
      `;
    }

    const productImage = document.querySelector('.product-video-add-to-cart-section .product-thumbnail');
    if (productImage) {
      productImage.src = featured_image;
    }

    // Create a container for the radio buttons (template-based, single append)
    const addToCartSection = document.querySelector('.product-video-add-to-cart-section');
    const existingWrapper = addToCartSection.querySelector('.variant-size-container');
    if (existingWrapper) existingWrapper.remove();
    const existingGroup = addToCartSection.querySelector('.variant-radio-group');
    if (existingGroup) existingGroup.remove();
    const existingLabel = addToCartSection.querySelector('.variant-size-label');
    if (existingLabel) existingLabel.remove();

    const variantsMarkup = processedVariants.map((variant, idx) => {
      const disabledAttr = variant.inventory_quantity <= 0 ? 'disabled' : '';
      const checkedAttr = idx === 0 ? 'checked' : '';
      const selectedClass = idx === 0 ? 'is-selected' : '';
      const labelText = processedVariants.length === 1 ? 'One Size' : (variant.title || variant.id);
      return `
        <label class="${selectedClass}" style="cursor: pointer;">
          <input type="radio" name="product-variant" value="${variant.id}" ${disabledAttr} ${checkedAttr}>
          ${processedVariants.length === 1 ? 'One Size' : variant.title}
        </label>
      `;
    }).join('');

    const productInfoSection = addToCartSection.querySelector('.product-info');
    if (productInfoSection) {
      const tpl = document.createElement('template');
      tpl.innerHTML = `
        <div class="variant-size-container flex-1">
          <legend class="h6 variant-size-label form__label mt-0 mb-0">Size</legend>
          <div class="variant-radio-group" style="margin: 0;">${variantsMarkup}</div>
        </div>
      `;
      productInfoSection.appendChild(tpl.content);
    }

    const radioContainer = addToCartSection.querySelector('.variant-radio-group');

    // Fallback for browsers without :has support — manage selected styles via class
    const updateSelectedClasses = () => {
      const labels = Array.from(radioContainer.querySelectorAll('label'));
      labels.forEach((label) => {
        const input = label.querySelector('input[type="radio"]');
        if (!input) return;
        label.classList.toggle('is-selected', input.checked);
      });
    };

    // Set up change handlers and initialize selection
    radioContainer.querySelectorAll('input[type="radio"]').forEach((input, idx) => {
      if (idx === 0 && input.checked) {
        input.closest('label')?.classList.add('is-selected');
      }
      input.addEventListener('change', updateSelectedClasses);
    });
  }

  setupVideoAddToCart(productMetaObj) {
    const { name, variants, price, imageUrl, url, title } = productMetaObj;

    if (!name && title) {
      return this.setupVideoAddToCartPDP(productMetaObj);
    }

    const processedVariants = this.processVariants(parseEncodedObject(variants));

    const [mainProductName, subProductName] = String(name || '').split(' - ');
    const productNameSection = document.querySelector('.product-video-add-to-cart-section .product-name');

    if (productNameSection) {
      productNameSection.href = url;
      productNameSection.innerHTML = `
        <h3>${mainProductName || ''}</h3>
        ${subProductName ? `<p class="sub-name">${subProductName}</p>` : ''}
        <p class="product-price mt-0">$${price} USD</p>
      `;
    }

    const productImage = document.querySelector('.product-video-add-to-cart-section .product-thumbnail');
    if (productImage) {
      productImage.src = imageUrl;
    }

    // Create a container for the radio buttons (template-based, single append)
    const addToCartSection = document.querySelector('.product-video-add-to-cart-section');
    const existingWrapper = addToCartSection.querySelector('.variant-size-container');
    if (existingWrapper) existingWrapper.remove();
    const existingGroup = addToCartSection.querySelector('.variant-radio-group');
    if (existingGroup) existingGroup.remove();
    const existingLabel = addToCartSection.querySelector('.variant-size-label');
    if (existingLabel) existingLabel.remove();

    const variantsMarkup = processedVariants.map((variant, idx) => {
      const disabledAttr = variant.inventory_quantity <= 0 ? 'disabled' : '';
      const checkedAttr = idx === 0 ? 'checked' : '';
      const selectedClass = idx === 0 ? 'is-selected' : '';
      const labelText = processedVariants.length === 1 ? 'One Size' : (variant.title || variant.id);
      return `
        <label class="${selectedClass}" style="cursor: pointer;">
          <input type="radio" name="product-variant" value="${variant.id}" ${disabledAttr} ${checkedAttr}>
          ${processedVariants.length === 1 ? 'One Size' : variant.title}
        </label>
      `;
    }).join('');

    const productInfoSection = addToCartSection.querySelector('.product-info');
    if (productInfoSection) {
      const tpl = document.createElement('template');
      tpl.innerHTML = `
        <div class="variant-size-container flex-1">
          <legend class="h6 variant-size-label form__label mb-0 mt-0">Size</legend>
          <div class="variant-radio-group" style="margin: 0;">${variantsMarkup}</div>
        </div>
      `;
      productInfoSection.appendChild(tpl.content);
    }

    const radioContainer = addToCartSection.querySelector('.variant-radio-group');

    // Fallback for browsers without :has support — manage selected styles via class
    const updateSelectedClasses = () => {
      const labels = Array.from(radioContainer.querySelectorAll('label'));
      labels.forEach((label) => {
        const input = label.querySelector('input[type="radio"]');
        if (!input) return;
        label.classList.toggle('is-selected', input.checked);
      });
    };

    // Set up change handlers and initialize selection
    radioContainer.querySelectorAll('input[type="radio"]').forEach((input, idx) => {
      if (idx === 0 && input.checked) {
        input.closest('label')?.classList.add('is-selected');
      }
      input.addEventListener('change', updateSelectedClasses);
    });
  }

  setupAddToCartTrigger() {
    const btn = document.querySelector('#product-video-add-to-cart-trigger');
    const modal = document.getElementById('product-video-modal');
    if (!btn || !modal) return;

    // Initialize once
    if (btn.dataset.atcInitialized !== 'true') {
      const openHandler = () => {
        modal.classList.add('atc-open');
        btn.dataset.atcStage = 'ready';
        // Swap handlers: open -> submit
        btn.removeEventListener('click', btn._openHandler);
        btn.addEventListener('click', btn._submitHandler);
      };

      const submitHandler = async () => {
        const btn = document.querySelector('#product-video-add-to-cart-trigger');
        if (!btn || btn.dataset.loading === 'true') return;
        const selected = document.querySelector('.product-video-add-to-cart-section input[name="product-variant"]:checked');
        if (!selected) {
          console.warn('No variant selected');
          return;
        }

        // Support both numeric and GID variant ids
        const rawId = String(selected.value || '').trim();
        const variantId = rawId.includes('/') ? rawId.split('/').pop() : rawId;

        btn.dataset.loading = 'true';
        btn.disabled = true;
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: { _add_source: 'video_modal' } }] })
          });
          if (!response.ok) throw new Error('Failed to add item to cart');

          if (typeof window.refreshCart === 'function') {
            window.refreshCart();
          }

          // Close the modal
          const modal = document.getElementById('product-video-modal');
          modal.close();
          this.resetAddToCartTrigger();
        } catch (err) {
          console.error('Add to cart error:', err);
        } finally {
          btn.dataset.loading = 'false';
          btn.disabled = false;
        }
      };

      btn._openHandler = openHandler;
      btn._submitHandler = submitHandler;
      btn.addEventListener('click', openHandler);
      btn.dataset.atcInitialized = 'true';
    }

    // Always reset to initial stage
    this.resetAddToCartTrigger();

    // Ensure close button works every time the section is opened
    const atcClose = document.getElementById('product-video-atc-close');
    if (atcClose) {
      if (atcClose._closeHandler) {
        atcClose.removeEventListener('click', atcClose._closeHandler);
      }
      atcClose._closeHandler = () => {
        const modal = document.getElementById('product-video-modal');
        modal?.classList.remove('atc-open');
        this.resetAddToCartTrigger();
      };
      atcClose.addEventListener('click', atcClose._closeHandler);
    }
  }

  resetAddToCartTrigger() {
    const btn = document.querySelector('#product-video-add-to-cart-trigger');
    const modal = document.getElementById('product-video-modal');
    if (!btn || !modal) return;
    btn.dataset.atcStage = 'closed';
    modal.classList.remove('atc-open');
    // Ensure only the open handler is attached once
    if (btn._submitHandler) {
      btn.removeEventListener('click', btn._submitHandler);
    }
    if (btn._openHandler) {
      btn.removeEventListener('click', btn._openHandler);
      btn.addEventListener('click', btn._openHandler);
    }
  }

  setupVideoTriggers() {

    const videoTriggers = this.container.querySelectorAll('.video-trigger');
    videoTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const productCard = trigger.closest('.product-card') ?? trigger;
        const productMeta = productCard?.dataset.product;
        const productMetaObj = JSON.parse(productMeta);
        this.setupVideoAddToCart(productMetaObj);

        const modal = document.getElementById('product-video-modal');

        // Derive videoId from data attributes
        let videoId = trigger.getAttribute('data-video-id');
        if (!videoId) {
          const videoUrl = trigger.getAttribute('data-video-url') || '';
          const m = videoUrl.match(/\/embed\/([^?&#/]+)/) || videoUrl.match(/[?&]v=([^?&#/]+)/);
          videoId = m ? m[1] : '';
        }
        if (!videoId) {
          console.warn('No video id found for video trigger');
          return;
        }

        // Ensure reset of listeners and section when the modal closes (ESC or programmatic)
        if (!modal.dataset.atcResetBound) {
          modal.addEventListener('close', () => {
            destroyYouTubePlayer();
            this.resetAddToCartTrigger();
          });
          modal.addEventListener('cancel', () => {
            destroyYouTubePlayer();
          });
          modal.dataset.atcResetBound = 'true';
        }

        // Show the modal then start the player
        modal.showModal();
        const shouldMute = window.innerWidth < 768; // ensure autoplay on mobile
        createOrLoadYouTubePlayer(videoId, { mute: shouldMute });

        // Add click event listener to close modal when clicking outside
        const handleBackdropClick = (e) => {
          if (e.target === modal) {
            modal.close();
            destroyYouTubePlayer();
            // Remove the event listener
            modal.removeEventListener('click', handleBackdropClick);

            const addToCartSection = document.querySelector('.product-video-add-to-cart-section');
            addToCartSection.classList.remove('open');
            this.resetAddToCartTrigger();

          }
        };

        modal.addEventListener('click', handleBackdropClick);

        // Top-right close button inside dialog chrome
        const modalCloseBtn = modal.querySelector('.product-video-modal-close');
        if (modalCloseBtn) {
          if (modalCloseBtn._closeHandler) {
            modalCloseBtn.removeEventListener('click', modalCloseBtn._closeHandler);
          }
          modalCloseBtn._closeHandler = () => {
            destroyYouTubePlayer();
            modal.close();
          };
          modalCloseBtn.addEventListener('click', modalCloseBtn._closeHandler);
        }

        // Wire close button inside ATC section
        const atcClose = document.getElementById('product-video-atc-close');
        if (atcClose) {
          const closeHandler = () => {
            modal.classList.remove('atc-open');
            this.resetAddToCartTrigger();
          };
          atcClose.addEventListener('click', closeHandler, { once: true });
        }
      });
    });
  }

  createVariantElements(variants, productId) {
    if (!variants?.length) return '';

    return variants.map((variant, index) => {
      const disabled = variant.inventory_quantity <= 0 ? 'disabled' : '';
      return `
        <input type="radio" id="${productId}-1-${index}" name="Size" value="${variant.title}" form="${productId}-single__cart-item" ${disabled}>
        <label for="${productId}-1-${index}">
          ${variants.length === 1 ? 'One Size' : variant.title}
        </label>
      `;
    }).join('');
  }

  createVideoTrigger(product) {
    if (!product.mfield_custom_product_video_id) return '';

    let videoUrl = `https://www.youtube.com/embed/${product.mfield_custom_product_video_id}?si=AVQ9yk8tbyO4MGPc&rel=0&modestbranding=1&autoplay=1&enablejsapi=1&controls=0&loop=1&vq=hd1080&quality=hd1080`;
    if (window.innerWidth < 768) {
      videoUrl += '&mute=1';
    }

    return `
      <button class="video-trigger" data-video-url="${videoUrl}" data-video-id="${product.mfield_custom_product_video_id}">
        <img src="https://cdn.shopify.com/s/files/1/1582/0805/files/BL101_Play_Button-Red.svg?v=1755265748" alt="Play Video" width="40" height="40">
      </button>
    `;
  }

  createVariantSection(variants, product) {
    if (!variants?.length) return '';

    const variantElements = this.createVariantElements(variants, product.uid);
    const variantsWithOptions = variants.map(variant => ({
      ...variant,
      options: [variant.title],
      id: variant.id.split('/').pop(),
    }));

    return `
      <div class="cart__badge" id="product_variant_id">
        <div class="cart__options">
          <options-radio class="cart__variants no-js-hidden" data-section="${product.uid}" data-url="/products/${product.handle}">
            <fieldset class="js product-form__input">
              ${variantElements}
              <span for="" class="more__items" style="display: none;" data-link="/products/${product.handle}">+</span>
            </fieldset>
            <script type="application/json" class="product-variants-dump updated">
              ${JSON.stringify(variantsWithOptions)}
            </script>
          </options-radio>
        </div>
      </div>
    `;
  }

  render(products, clearContainer = false) {
    if (clearContainer) {
      this.clearContainer();
    }

    products.forEach(product => {
      if (!product) return;

      if (product.isMerchandising) {
        let cleanContent = product.content;
        const scriptRegex = /^<script[^>]*>([\s\S]*)<\/script>$/i;
        const match = scriptRegex.exec(cleanContent);
        if (match && match[1]) {
          cleanContent = match[1].trim();
        }
        const li = document.createElement('li');
        li.classList.add('js__grid-item', 'product-card', 'merchandising-card');
        li.innerHTML = cleanContent;
        this.container.appendChild(li);
        return;
      }

      // for blog card
      if (product.author) {
        const li = document.createElement('li');
        li.classList.add('js__grid-item', 'product-card', 'blog-card');
        li.innerHTML = `
          <a href="${product.url}" class="card-wrapper">
            <div class="card card--blog card--search card--soft">
              <div class="card__inner">
                <div class="media media--cropped">
                  <img
                    src="${product.imageUrl}"
                    loading="lazy"
                    width="600"
                    height="600"
                    alt="${product.name}"
                  >
                  </div>
                <div class="card__content">
                  <h3 class="card__text h3">${ product.name }</h3>
                  <div class="card-article-info caption-with-letter-spacing h5">
                    <span class="circle-divider">${product.ss_date_display}</span>
                  </div>
                </div>
                <div class="card__badge">
                  <span class="badge color-background-1">Blog</span>
                </div>
              </div>
            </div>
          </a>
        `;
        this.container.appendChild(li);
        return;
      }

      const variants = this.processVariants(parseEncodedObject(product.variants));
      const { productName, productColor } = this.getProductNameAndColor(product.name);
      const images = this.formatImages(product.images, product.images_alt?.split('|'), product.name);
      const variantSection = this.createVariantSection(variants, product);

      const productPriceRange = [];
      variants.forEach(variant => {
        const amount = variant.price?.amount || variant.price;
        if (productPriceRange.length === 0 && amount) {
          productPriceRange.push(amount);
        } else if (Number(amount) < Number(productPriceRange[0])) {
          productPriceRange.unshift(amount);
        } else if (Number(amount) > Number(productPriceRange[productPriceRange.length - 1])) {
          productPriceRange.push(amount);
        }
      })
      let productPrice = product.price;
      if (productPriceRange.length > 1) {
        productPrice = productPriceRange;
      }

      const priceSection = this.createPriceSection(product.msrp, productPrice, product.uid, product.collection_handle);
      const cardBadge = this.createCardBadge(product, variants);
      const swatches = this.createSwatchesSection(product);
      const videoTrigger = this.createVideoTrigger(product);

      const productCard = document.createElement('li');
      productCard.classList.add('js__grid-item', 'product-card');
      productCard.dataset.product = JSON.stringify(product);
      productCard.href = `/products/${product.handle}`;

      productCard.innerHTML = `
        <div class="card-wrapper">
        ${videoTrigger}
          <div class="card-information">
            <div class="card-information__wrapper">
              <h3 class="card-information__text h5">
                <a href="/products/${product.handle}" class="full-unstyled-link" onmousedown="IntelliSuggest.clickItem(this, { siteId: '40t3km', intellisuggestData: '${product.intellisuggestData}', intellisuggestSignature: '${product.intellisuggestSignature}' })">${productName}${productColor}</a>
              </h3>
              <span class="caption-large light"></span>

              <div class="price">
                <div>
                  ${priceSection}
                  <small class="unit-price caption hidden">
                    <span class="visually-hidden">Unit price</span>
                    <span class="price-item price-item--last">
                      <span></span>
                      <span>/</span>
                      <span class="visually-hidden">&nbsp;per&nbsp;</span>
                      <span></span>
                    </span>
                  </small>
                </div>
              </div>
              ${swatches}
              <product-form class="product-form cart__form-button">
                <div class="product-form__error-message-wrapper" role="alert" hidden="">
                  <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-error" viewBox="0 0 13 13">
                    <circle cx="6.5" cy="6.50049" r="5.5" stroke="white" stroke-width="2"></circle>
                    <circle cx="6.5" cy="6.5" r="5.5" fill="#EB001B" stroke="#EB001B" stroke-width="0.7"></circle>
                    <path d="M5.87413 3.52832L5.97439 7.57216H7.02713L7.12739 3.52832H5.87413ZM6.50076 9.66091C6.88091 9.66091 7.18169 9.37267 7.18169 9.00504C7.18169 8.63742 6.88091 8.34917 6.50076 8.34917C6.12061 8.34917 5.81982 8.63742 5.81982 9.00504C5.81982 9.37267 6.12061 9.66091 6.50076 9.66091Z" fill="white"></path>
                    <path d="M5.87413 3.17832H5.51535L5.52424 3.537L5.6245 7.58083L5.63296 7.92216H5.97439H7.02713H7.36856L7.37702 7.58083L7.47728 3.537L7.48617 3.17832H7.12739H5.87413ZM6.50076 10.0109C7.06121 10.0109 7.5317 9.57872 7.5317 9.00504C7.5317 8.43137 7.06121 7.99918 6.50076 7.99918C5.94031 7.99918 5.46982 8.43137 5.46982 9.00504C5.46982 9.57872 5.94031 10.0109 6.50076 10.0109Z" fill="white" stroke="#EB001B" stroke-width="0.7"></path>
                  </svg>
                  <span class="product-form__error-message"></span>
                </div>
                <form method="post" action="/cart/add" id="product-form-${product.uid}" accept-charset="UTF-8" class="form product-form product-cart__form" enctype="multipart/form-data" data-productid="${product.uid}" novalidate="novalidate" data-type="add-to-cart-form">
                  <input type="hidden" name="form_type" value="product">
                  <input type="hidden" name="utf8" value="✓">
                  <input type="hidden" name="id" data-productid="${product.uid}" value="${product.ss_id}" data-variantname="null">
                  <div class="product-form__buttons">
                    <button type="submit" name="add" class="product-form__submit button button--full-width button--primary" ${(variants && variants.length > 1) || product.ss_available === "0" ? 'disabled' : ''}>
                      <span>${(variants && variants.length > 1) ? 'Select a Size' : product.ss_available === "0" ? 'Sold Out' : 'Add to Cart'}</span>
                      <div class="loading-overlay__spinner hidden">
                        <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                        </svg>
                      </div>
                    </button>
                  </div>
                  <input type="hidden" name="product-id" value="${product.uid}">
                </form>
              </product-form>
            </div>
          </div>

          <div class="card card--product card--outline" tabindex="-1">
            <div class="card__inner">

              <div>
                <div class="media media-grey-background media--adapt media--hover-effect" style="padding-bottom: 100.0%;">
                  <img src="${images[0].url}" alt="${images[0].alt}" loading="lazy" class="motion-reduce main-product-image" width="600" height="600">
                  ${images[1] && images[1].url !== images[0].url ? `<img src="${images[1].url}" alt="${images[1].alt}" loading="lazy" class="motion-reduce secondary-product-image" width="600" height="600">` : ''}
                </div>
              </div>
              <div class="card__badge">
                ${cardBadge}
              </div>
              ${variantSection}
            </div>
          </div>
        </div>
      `;

      this.container.appendChild(productCard);
    });

    // Setup video triggers after rendering all products
    this.setupVideoTriggers();
    this.setupAddToCartTrigger();
  }
}

window.ProductCards = ProductCards;

// Pre-instantiate a lightweight YouTube player on page load to reduce first-open latency
function prewarmYouTubePlayer() {
  try {
    ensureYouTubeApiScriptPresent();
    const container = ensurePlayerContainerExists();
    if (!container) return;
    if (window._productVideoPlayer) return;
    ytReadyPromise.then(() => {
      if (window._productVideoPlayer) return;
      window._productVideoPlayer = new window.YT.Player('product-video-player', {
        // Use YouTube sample ID; autoplay disabled, so it won't play
        videoId: 'M7lc1UVf-VE',
        width: '100%',
        height: '100%',
        playerVars: {
          autoplay: 0,
          controls: 0,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          enablejsapi: 1,
        },
        events: {
          onReady: (event) => {
            try { event.target.mute(); event.target.stopVideo(); } catch (_) {}
          },
        },
      });
    });
  } catch (_) {}
}

if (document.readyState === 'complete' || document.readyState === 'interactive') {
  prewarmYouTubePlayer();
} else {
  window.addEventListener('DOMContentLoaded', prewarmYouTubePlayer);
}